import { buttonVariants } from "@forge/ui";
import {
  createFileRoute,
  isRedirect,
  Link,
  redirect,
} from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { LandingFaq } from "../components/landing-faq";
import { LandingPricing } from "../components/landing-pricing";
import { ProductShowcase } from "../components/product-frames";
import { SiteFooter } from "../components/site-footer";
import { trackLandingCta } from "../lib/analytics";
import { loadSession } from "../lib/auth";
import { SITE_NAME } from "../lib/site";

export const Route = createFileRoute("/")({
  beforeLoad: async ({ context }) => {
    try {
      await loadSession(context.queryClient);
      throw redirect({ to: "/home" });
    } catch (error) {
      if (isRedirect(error)) throw error;
    }
  },
  component: LandingPage,
});

const PILLARS = [
  {
    kicker: "01",
    title: "Estoque no centro",
    body: "Um SKU, vários anúncios, kits com composição. A venda baixa o que importa — não o que o Mercado Livre acha que você tem.",
  },
  {
    kicker: "02",
    title: "Pedido com rastro",
    body: "Pago, enviado, entregue. Timeline de envio, perguntas e mensagens da loja no mesmo lugar, sem abrir o painel do Mercado Livre a cada ping.",
  },
  {
    kicker: "03",
    title: "Loja nova em três passos",
    body: "Autoriza a loja, importa anúncios e pedidos, vincula o estoque. O restante da operação já está montado.",
  },
];

function LandingPage() {
  return (
    <div className="min-h-dvh bg-surface text-ink">
      <header className="sticky top-0 z-40 border-b border-line/80 bg-surface/85 backdrop-blur-md">
        <div className="mx-auto flex h-12 max-w-5xl items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-control bg-ember text-ink-inverse">
              <Flame className="size-3.5" />
            </span>
            <span className="text-[0.8125rem] font-semibold tracking-tight">
              {SITE_NAME}
            </span>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a
              href="#produto"
              className="hidden px-2.5 text-2xs font-medium text-ink-secondary transition-colors duration-150 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink sm:inline"
            >
              Produto
            </a>
            <a
              href="#precos"
              className="hidden px-2.5 text-2xs font-medium text-ink-secondary transition-colors duration-150 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink sm:inline"
            >
              Preços
            </a>
            <Link
              to="/login"
              className="px-2.5 text-2xs font-medium text-ink-secondary transition-colors duration-150 ease-swift [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink"
            >
              Entrar
            </Link>
            <Link
              to="/signup"
              onClick={() => trackLandingCta("header")}
              className={buttonVariants({ size: "sm" })}
            >
              Começar trial
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 size-112 rounded-full bg-ember/15 blur-3xl dark:bg-ember/10"
          />
          <div className="relative mx-auto max-w-5xl px-5 py-16 sm:py-20">
            <p className="font-mono text-2xs tracking-[0.18em] text-ember-strong uppercase">
              Feito para o Mercado Livre
            </p>
            <h1 className="mt-3 max-w-2xl text-[2.15rem] leading-[1.12] font-semibold tracking-tight sm:text-5xl">
              A operação da loja,
              <span className="text-ember whitespace-nowrap">
                {" "}
                sem o vai-e-volta
              </span>{" "}
              do Mercado Livre.
            </h1>
            <p className="mt-4 max-w-lg text-[0.9375rem] leading-relaxed text-ink-secondary">
              Estoque central, pedidos, perguntas, mensagens e lucro numa
              tela só. Feito para quem vende no Mercado Livre e não quer mais
              planilha, automação frágil ou três abas do painel abertas.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/signup"
                onClick={() => trackLandingCta("hero")}
                className={buttonVariants({ size: "lg" })}
              >
                Começar 14 dias grátis
              </Link>
              <Link
                to="/login"
                className="text-2xs font-medium text-ink-muted underline-offset-2 [@media(hover:hover)_and_(pointer:fine)]:hover:text-ink [@media(hover:hover)_and_(pointer:fine)]:hover:underline"
              >
                Já tenho conta
              </Link>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-1.5 text-2xs text-ink-muted">
              <li>Autoriza a loja</li>
              <li>Importa anúncios e pedidos</li>
              <li>Mapeia o SKU</li>
              <li className="text-ember-strong">14 dias, sem cartão</li>
            </ul>
          </div>
        </section>

        <section
          id="produto"
          className="scroll-mt-16 border-y border-line bg-surface-sunken"
        >
          <div className="mx-auto max-w-5xl px-5 py-12 sm:py-14">
            <p className="font-mono text-2xs tracking-[0.18em] text-ink-faint uppercase">
              O produto
            </p>
            <h2 className="mt-2 max-w-lg text-xl font-semibold tracking-tight">
              Hoje, Lucro e Painel — as três perguntas da operação, no visual
              que o vendedor vai usar.
            </h2>
            <div className="mt-6">
              <ProductShowcase />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-5 py-16">
          <p className="font-mono text-2xs tracking-[0.18em] text-ink-faint uppercase">
            O que fica no centro
          </p>
          <div className="mt-6 grid gap-px bg-line sm:grid-cols-3">
            {PILLARS.map((pillar) => (
              <article
                key={pillar.kicker}
                className="bg-surface px-5 py-6 sm:px-6"
              >
                <p className="font-mono text-2xs text-ember">{pillar.kicker}</p>
                <h2 className="mt-2 text-base font-semibold tracking-tight">
                  {pillar.title}
                </h2>
                <p className="mt-2 text-2xs leading-relaxed text-ink-secondary">
                  {pillar.body}
                </p>
              </article>
            ))}
          </div>
        </section>

        <LandingPricing />
        <LandingFaq />

        <section className="border-y border-line bg-surface-sunken">
          <div className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-14 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">
                Abre a conta. Conecta a loja. Opera.
              </h2>
              <p className="mt-1 text-2xs text-ink-muted">
                Sem cartão no trial. Sem lista de espera.
              </p>
            </div>
            <Link
              to="/signup"
              onClick={() => trackLandingCta("footer")}
              className={buttonVariants({ size: "lg" })}
            >
              Criar conta
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
